import { CSSProperties, useCallback, useEffect, useState } from "react";
import { ExternalLink, ChevronLeft, ChevronRight, X } from "lucide-react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { Button } from "../../components/ui/button";

interface IProps {
  src: string[];
  currentIndex?: number;
  backgroundStyle?: CSSProperties;
  disableScroll?: boolean;
  closeOnClickOutside?: boolean;
  onClose?: () => void;
  closeComponent?: JSX.Element;
  leftArrowComponent?: JSX.Element;
  rightArrowComponent?: JSX.Element;
}

const ImageViewer = (props: IProps) => {
  const [currentIndex, setCurrentIndex] = useState(props.currentIndex ?? 0);

  const changeImage = useCallback(
    (delta: number) => {
      let nextIndex = (currentIndex + delta) % props.src.length;
      if (nextIndex < 0) nextIndex = props.src.length - 1;
      setCurrentIndex(nextIndex);
    },
    [currentIndex, props.src.length]
  );

  const handleClick = useCallback(
    (event: any) => {
      if (!event.target || !props.closeOnClickOutside) {
        return;
      }

      const checkId = event.target.id === 'ReactSimpleImageViewer';
      const checkClass = event.target.classList.contains('react-simple-image-viewer__slide');

      if (checkId || checkClass) {
        event.stopPropagation();
        props.onClose?.();
      }
    },
    [props.onClose, props.closeOnClickOutside]
  );

  const handleKeyDown = useCallback(
    (event: any) => {
      if (event.key === "Escape") {
        props.onClose?.();
      }

      if (["ArrowLeft", "h"].includes(event.key)) {
        changeImage(-1);
      }

      if (["ArrowRight", "l"].includes(event.key)) {
        changeImage(1);
      }
    },
    [props.onClose, changeImage]
  );

  const handleWheel = useCallback(
    (event: any) => {
      if (event.wheelDeltaY > 0) {
        changeImage(-1);
      } else {
        changeImage(1);
      }
    },
    [changeImage]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    if (!props.disableScroll) {
      document.addEventListener("wheel", handleWheel);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      if (!props.disableScroll) {
        document.removeEventListener("wheel", handleWheel);
      }
    };
  }, [handleKeyDown, handleWheel, props.disableScroll]);

  function backToOriginalImageSize() {
    let elements = document.getElementsByClassName("react-transform-component") as HTMLCollectionOf<HTMLElement>
    for (let i = 0; i < elements.length; i++) {
      elements[i].style.transform = 'scale(1)';
    }
  }

  return (
    <div
      id="ReactSimpleImageViewer"
      className="fixed inset-0 z-50 flex items-center justify-center bg-viewer user-select-none"
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      style={props.backgroundStyle}
    >
      <div className="absolute top-5 right-5 z-[51] flex gap-2">
        <a href={`${props.src[currentIndex]}`} target="_blank" download rel="noreferrer">
          <Button variant="ghost" size="icon" className="bg-overlay-control hover:bg-overlay-control-hover text-overlay-control-text rounded-full h-10 w-10">
            <ExternalLink className="w-5 h-5" />
          </Button>
        </a>
        <Button
          variant="ghost"
          size="icon"
          className="bg-overlay-control hover:bg-overlay-control-hover text-overlay-control-text rounded-full h-10 w-10"
          onClick={() => props.onClose?.()}
        >
          <X className="w-6 h-6" />
        </Button>
      </div>

      {props.src.length > 1 && (
        <div
          className="absolute left-0 z-50 h-[80%] flex items-center justify-center px-4 cursor-pointer text-overlay-control-text opacity-20 hover:opacity-100 transition-opacity text-5xl font-bold select-none"
          onClick={(e) => { e.stopPropagation(); changeImage(-1); backToOriginalImageSize() }}
        >
          {props.leftArrowComponent || <ChevronLeft className="w-16 h-16" />}
        </div>
      )}

      {props.src.length > 1 && (
        <div
          className="absolute right-0 z-50 h-[80%] flex items-center justify-center px-4 cursor-pointer text-overlay-control-text opacity-20 hover:opacity-100 transition-opacity text-5xl font-bold select-none"
          onClick={(e) => { e.stopPropagation(); changeImage(1); backToOriginalImageSize() }}
        >
          {props.rightArrowComponent || <ChevronRight className="w-16 h-16" />}
        </div>
      )}

      <div
        className="w-full h-full flex items-center justify-center"
        onClick={handleClick}
      >
        <div className="react-simple-image-viewer__slide w-full h-full flex items-center justify-center">
          <TransformWrapper
            limitToBounds={false}
            minScale={0.5}
            maxScale={5}
            centerOnInit={true}
            wheel={{ step: 0.03 }}
            doubleClick={{ mode: "reset" }}
          >
            <TransformComponent
              wrapperStyle={{ width: "100%", height: "100%" }}
              contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <img
                className="max-h-[90vh] max-w-[90vw] object-contain select-none"
                id="image"
                src={props.src[currentIndex]}
                alt=""
              />
            </TransformComponent>
          </TransformWrapper>
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;